/* =============================================================================
 *  AndreiChatPanel
 *
 *  Daily-use chat surface for talking to Andrei — the default in-app
 *  Virtual Employee. Chat-only: opened from the header [andrei N] button.
 *  The pending queue, activity log, and per-action policy settings live in
 *  the Settings page (AndreiAgentPanel modal) so the chat isn't competing
 *  for tabs/space with admin chrome.
 *
 *  Bus: writes user messages to `andrei_messages`. The
 *  `onAndreiMessageCreated` Firestore trigger picks them up, calls the
 *  Gemini/Anthropic chat service (with the Andrei VirtualEmployee record's
 *  systemPrompt + tool catalog), and writes the assistant reply back as a
 *  doc whose `status` flips from 'typing' → 'done'. The panel renders
 *  whichever state is current.
 * ============================================================================= */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addDoc, collection, doc, getDocs, limit, onSnapshot, orderBy, query,
  serverTimestamp, setDoc, Timestamp, where,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { authFetch } from '../../utils/cloudFunctionsAuth';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAndreiChat } from '../../contexts/AndreiChatContext';
import { ProviderSelect } from '../../components/chat/ProviderSelect';
import './AndreiChatPanel.scss';

const DECIDE_ACTION_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/decideAgentPendingAction';
const SPEND_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/getAndreiSpend';
const COMPACT_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/triggerAndreiCompactionHttp';

interface SpendInfo { sinceReset: number; total: number; limit: number | null; compactionModel: string; resetAt: number | null; error?: string; }

/** Compact USD for the header chip: '$2.41', '<$0.01' for dust, '$0.00' for zero. */
function fmtUsd(n: number): string {
  if (!n || n <= 0) return '$0.00';
  if (n < 0.01) return '<$0.01';
  return `$${n.toFixed(2)}`;
}
/** 'gemini:gemini-flash-latest' → 'Gemini Flash'; best-effort prettify. */
function prettyModel(id: string): string {
  if (!id) return 'unknown';
  const tail = String(id).split(':').pop()!.split('/').pop()!;
  return tail.replace(/-latest$/, '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
// Per-file attachment cap. Generous for screenshots/PDFs; well below the
// 32 MiB Firestore doc limit even if every chat message has one.
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

interface ChatMessage {
  id: string;
  from: 'user' | 'assistant';
  text: string;
  ts?: Timestamp;
  channel?: string;
  processed?: boolean;
  inReplyTo?: string;
  toolCalls?: Array<{ name: string; args?: any; result?: any; error?: string }>;
  error?: string;
  status?: 'typing' | 'done' | 'error';
  // User-attached files (drag-drop or paste). Stored in Firebase Storage
  // under andrei_attachments/{uid}/...; the URL is a signed download link.
  attachments?: Array<{
    name:        string;
    size:        number;
    contentType: string;
    url:         string;
    storagePath: string;
  }>;
}

interface PendingAction {
  id: string;
  kind: string;
  target?: Record<string, any>;
  payload?: Record<string, any>;
  reason?: string;
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'error';
  createdAt?: Timestamp;
  expiresAt?: Timestamp;
}

// Local pending-attachment record (pre-send). Once uploaded it carries
// the public download URL + storage path the message doc references.
interface PendingAttachment {
  id:           string;       // local uid for keying + remove
  name:         string;
  size:         number;
  contentType:  string;
  // Set after upload succeeds.
  url?:         string;
  storagePath?: string;
  // Upload progress / error state.
  uploading?:   boolean;
  error?:       string;
}

// No props — the panel is mounted at App level and reads its open/close
// state plus context handle from AndreiChatContext. Any component can
// trigger it via `useAndreiChat().openAndreiChat({ context, prefill })`.

function tsToDate(ts: any): Date | null {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (typeof ts._seconds === 'number')  return new Date(ts._seconds  * 1000);
  if (typeof ts.seconds  === 'number')  return new Date(ts.seconds   * 1000);
  if (ts instanceof Date) return ts;
  return null;
}

// Lazy AudioContext — created on first ping. Browsers may block audio
// before the first user interaction; failures are silent (try/catch).
let _audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext | null {
  try {
    if (_audioCtx) return _audioCtx;
    const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    _audioCtx = new Ctor();
    return _audioCtx;
  } catch {
    return null;
  }
}

// Bright two-note sparkle for new Andrei replies. Distinct from email
// pings so the user can tell them apart by ear.
async function playReplyChime(): Promise<void> {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
  } catch { return; }

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.13;
  master.connect(ctx.destination);

  const tone = (at: number, dur: number, freq: number, peak: number) => {
    const osc = ctx.createOscillator();
    const g   = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, now + at);
    g.gain.exponentialRampToValueAtTime(peak, now + at + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, now + at + dur);
    osc.connect(g).connect(master);
    osc.start(now + at);
    osc.stop(now + at + dur + 0.02);
  };

  tone(0.00, 0.14, 1046.5, 0.95); // C6
  tone(0.09, 0.16, 1318.5, 0.85); // E6
}

/** Reactive matchMedia boolean (SSR-safe). */
function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatch(mql.matches);
    setMatch(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return match;
}

/** Two distinct mobile signals, deliberately NOT conflated:
 *   - coarse: a touch pointer → a soft keyboard, no Shift key, no hover. Drives
 *     Enter=newline, no autofocus-pop, coarse-aware placeholder, keyboard-ride.
 *   - narrow: ≤640px viewport → the panel is a full-screen sheet. Drives layout
 *     chrome (hide float toggle, body scroll-lock).
 *  Keeping them separate means a MOUSE user who narrows their window doesn't
 *  suddenly lose Enter-to-send. */
function useMobileSignals(): { isCoarse: boolean; isNarrow: boolean } {
  const isCoarse = useMediaQuery('(pointer: coarse)');
  const isNarrow = useMediaQuery('(max-width: 640px)');
  return { isCoarse, isNarrow };
}

const AndreiChatPanel: React.FC = () => {
  const {
    isOpen: show,
    closeAndreiChat: onHide,
    context,
    sessionId,
    consumePrefill,
    setContext,
    setSessionId,
  } = useAndreiChat();

  // Mobile-first signals — coarse (touch keyboard/focus) vs narrow (layout).
  const { isCoarse, isNarrow } = useMobileSignals();

  // Auth gate: the panel is mounted at App level so it lives on every
  // route, including the login page. We render nothing until Firebase
  // confirms an authenticated user, and on sign-out we close + reset
  // local state so messages/inputs don't linger across sessions.
  const [authedUid, setAuthedUid] = useState<string | null>(() => getAuth().currentUser?.uid ?? null);
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u: User | null) => {
      setAuthedUid(u?.uid ?? null);
      if (!u) {
        onHide();
        setContext(null);
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live tail (last 100 messages, real-time) and older paged-in messages
  // are stored separately so the snapshot replacing liveMessages doesn't
  // wipe the older history the user has scrolled to load.
  const [liveMessages,  setLiveMessages]  = useState<ChatMessage[]>([]);
  const [olderMessages, setOlderMessages] = useState<ChatMessage[]>([]);
  const [hasMoreOlder,  setHasMoreOlder]  = useState(true);
  const [loadingOlder,  setLoadingOlder]  = useState(false);

  // Merge older + live, dedupe by id. Both arrive ASC (we reverse the
  // desc query for older; the live snapshot is reversed in its handler).
  const messages = useMemo<ChatMessage[]>(() => {
    const seen = new Set<string>();
    const all: ChatMessage[] = [];
    for (const m of olderMessages) { if (!seen.has(m.id)) { seen.add(m.id); all.push(m); } }
    for (const m of liveMessages)  { if (!seen.has(m.id)) { seen.add(m.id); all.push(m); } }
    return all;
  }, [olderMessages, liveMessages]);

  const [chatInput, setChatInput]     = useState('');
  const [sendingChat, setSendingChat] = useState(false);

  // Pull the prefill (if any) when the panel opens. consumePrefill clears
  // the slot so re-renders don't keep stomping on what the user typed.
  useEffect(() => {
    if (!show) return;
    const prefill = consumePrefill();
    if (prefill) setChatInput(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  const scrollRef    = useRef<HTMLDivElement | null>(null);
  const textareaRef  = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Floating mode (added 2026-05) ────────────────────────────────────
  // The chat opens docked to the right (the existing slide-out) by
  // default. The "[float]" button in the header transitions it to a
  // free-floating window that the user can drag + resize, and that
  // persists across page navigation (the whole panel is mounted at
  // App level so it survives route changes automatically — we just
  // need to persist position/size to localStorage so it survives a
  // page reload too).
  //
  // Storage: two keys keep mode and rect independently so toggling
  // back to docked doesn't wipe the user's preferred float position.
  const FLOAT_KEY_MODE = 'andrei.float.mode';
  const FLOAT_KEY_RECT = 'andrei.float.rect';
  const FLOAT_KEY_MINIMIZED = 'andrei.float.minimized';
  const PROVIDER_KEY = 'andrei.provider';
  const DEFAULT_FLOAT_RECT = { x: -1, y: 80, w: 480, h: 600 };

  const [isFloating, setIsFloating] = useState<boolean>(() => {
    try { return localStorage.getItem(FLOAT_KEY_MODE) === 'floating'; }
    catch { return false; }
  });
  // Provider override (added 2026-05): the chat trigger reads
  // `provider` off each outgoing andrei_messages doc to decide which
  // LLM to use. Default is Anthropic Sonnet 4.6. The dropdown in the
  // header lets the user switch (e.g. when an Anthropic spend cap is
  // hit, fall back to Gemini Pro).
  //
  // The option list + OpenRouter fetch logic live in
  // `useChatProviderOptions` so they're shared with the JobWorkspace
  // chat header. Provider type stays widened to string so any
  // `openrouter:<provider>/<model>` value works without a code change.
  type AndreiProvider = string;
  const [provider, setProvider] = useState<AndreiProvider>(() => {
    try {
      // One-time cleanup (2026-06): the dropdown used to persist in
      // localStorage, which silently pinned a model FOREVER and overrode
      // the AI/LLM settings page — the user had no idea why settings
      // changes did nothing. Overrides are now SESSION-scoped: pick a
      // model and it holds for this tab only; every fresh visit follows
      // the settings page again.
      localStorage.removeItem(PROVIDER_KEY);
      const stored = sessionStorage.getItem(PROVIDER_KEY);
      if (stored && stored.trim().startsWith('openrouter:')) return stored.trim();
    } catch { /* ignore */ }
    return ''; // '' → no hint → server uses the andreiChat settings default
  });
  useEffect(() => {
    try { sessionStorage.setItem(PROVIDER_KEY, provider); }
    catch { /* ignore */ }
  }, [provider]);

  // Minimize state — collapses the floating panel to just its header
  // bar so the rest of the page underneath is clickable. The user
  // reported (2026-05) that the floating panel's footprint silently
  // blocks dashboard interactions; minimize is the lighter alternative
  // to docking or closing.
  // Only meaningful when isFloating === true; docked mode ignores it.
  const [isMinimized, setIsMinimized] = useState<boolean>(() => {
    try { return localStorage.getItem(FLOAT_KEY_MINIMIZED) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(FLOAT_KEY_MINIMIZED, isMinimized ? '1' : '0'); }
    catch { /* ignore */ }
  }, [isMinimized]);
  // If the user docks while minimized, drop the minimize state too —
  // docked + minimized is a meaningless combination and the user
  // probably means "go back to normal".
  useEffect(() => {
    if (!isFloating && isMinimized) setIsMinimized(false);
  }, [isFloating, isMinimized]);
  const [floatRect, setFloatRect] = useState<{ x: number; y: number; w: number; h: number }>(() => {
    try {
      const raw = localStorage.getItem(FLOAT_KEY_RECT);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          return {
            x: Number.isFinite(parsed.x) ? parsed.x : DEFAULT_FLOAT_RECT.x,
            y: Number.isFinite(parsed.y) ? parsed.y : DEFAULT_FLOAT_RECT.y,
            w: Number.isFinite(parsed.w) && parsed.w >= 320 ? parsed.w : DEFAULT_FLOAT_RECT.w,
            h: Number.isFinite(parsed.h) && parsed.h >= 300 ? parsed.h : DEFAULT_FLOAT_RECT.h,
          };
        }
      }
    } catch { /* ignore */ }
    return DEFAULT_FLOAT_RECT;
  });

  // Persist mode + rect. We persist mode separately so toggling back to
  // docked doesn't clobber the user's preferred float position.
  useEffect(() => {
    try { localStorage.setItem(FLOAT_KEY_MODE, isFloating ? 'floating' : 'docked'); }
    catch { /* ignore quota / private-mode errors */ }
  }, [isFloating]);
  useEffect(() => {
    try { localStorage.setItem(FLOAT_KEY_RECT, JSON.stringify(floatRect)); }
    catch { /* ignore */ }
  }, [floatRect]);

  // First time we enter floating mode (no x stored yet — sentinel -1),
  // compute a sensible default position near the right edge so the
  // transition isn't jarring. Once the user moves the window, x will
  // be a real number and this branch never runs again.
  useEffect(() => {
    if (isFloating && floatRect.x < 0 && typeof window !== 'undefined') {
      const margin = 20;
      const x = Math.max(margin, window.innerWidth - floatRect.w - margin);
      const y = Math.max(margin, Math.min(floatRect.y, window.innerHeight - 200));
      setFloatRect((r) => ({ ...r, x, y }));
    }
  }, [isFloating, floatRect.x, floatRect.w, floatRect.y]);

  // Drag state — refs (not state) so the move handler doesn't trigger
  // re-renders on every pointermove. The actual position update IS in
  // state so the panel re-renders to the new spot.
  const dragRef = useRef<{ pid: number; startX: number; startY: number; rx: number; ry: number } | null>(null);
  const resizeRef = useRef<{ pid: number; startX: number; startY: number; rw: number; rh: number } | null>(null);

  const handleHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isFloating) return;
    // Skip drag if the user clicked an interactive child (button etc).
    const target = e.target as HTMLElement;
    if (target.closest('button, input, textarea, a, [data-no-drag]')) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      pid: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      rx: floatRect.x,
      ry: floatRect.y,
    };
  };
  const handleHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pid !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    setFloatRect((r) => {
      const nx = Math.max(0, Math.min(d.rx + dx, window.innerWidth - 80));
      const ny = Math.max(0, Math.min(d.ry + dy, window.innerHeight - 40));
      return { ...r, x: nx, y: ny };
    });
  };
  const handleHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pid === e.pointerId) {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      dragRef.current = null;
    }
  };

  const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isFloating) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    resizeRef.current = {
      pid: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      rw: floatRect.w,
      rh: floatRect.h,
    };
  };
  const handleResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = resizeRef.current;
    if (!r || r.pid !== e.pointerId) return;
    const dw = e.clientX - r.startX;
    const dh = e.clientY - r.startY;
    setFloatRect((rect) => {
      const nw = Math.max(320, Math.min(r.rw + dw, window.innerWidth - rect.x - 8));
      const nh = Math.max(300, Math.min(r.rh + dh, window.innerHeight - rect.y - 8));
      return { ...rect, w: nw, h: nh };
    });
  };
  const handleResizePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (resizeRef.current?.pid === e.pointerId) {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      resizeRef.current = null;
    }
  };

  // Re-clamp the float rect into viewport on window resize so the
  // window doesn't drift off-screen if the user shrinks the browser
  // after positioning the panel near an edge.
  useEffect(() => {
    if (!isFloating) return;
    const onResize = () => {
      setFloatRect((r) => ({
        x: Math.max(0, Math.min(r.x, window.innerWidth - 80)),
        y: Math.max(0, Math.min(r.y, window.innerHeight - 40)),
        w: Math.max(320, Math.min(r.w, window.innerWidth - 20)),
        h: Math.max(300, Math.min(r.h, window.innerHeight - 20)),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isFloating]);

  // Andrei is a cloud function — always available when the user is signed
  // in. There's no sidecar process to track, so we don't gate sending on
  // a heartbeat. (Steve had this gate when an external OpenClaw process
  // had to be running; that's gone.) `agentReady` exists only so the
  // composer can disable input briefly while auth is resolving.
  const agentReady = !!authedUid;

  // Pending-action tray: live subscription to agent_pending_actions, only
  // open items, oldest first (so the queue order is FIFO in the tray).
  const [pending, setPending]     = useState<PendingAction[]>([]);
  const [actingId, setActingId]   = useState<string | null>(null);
  useEffect(() => {
    if (!show || !authedUid) return;
    const q = query(
      collection(db, 'agent_pending_actions'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc'),
      limit(50),
    );
    const unsub = onSnapshot(q, (snap) => {
      setPending(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    }, (err) => {
      console.error('[AndreiChatPanel] pending subscribe failed:', err);
    });
    return () => unsub();
  }, [show, authedUid]);

  // ── Voice input (Web Speech API) ────────────────────────────────────
  // Tap the mic to start; speech is appended to whatever's already in the
  // textarea (interim results stream into the box live). Tap again to
  // stop. Browser support: Chrome/Edge/Safari ✓ — Firefox ✗ (we hide
  // the button there). Costs nothing — the browser does the recognition.
  const speechRecognitionAvailable = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
  }, []);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
  };

  const startListening = () => {
    if (isListening || !speechRecognitionAvailable) return;
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = (navigator.language || 'en-US');
    r.interimResults = true;
    r.continuous = true;
    // Anchor the input at the moment we start recording — speech is
    // appended after the existing text rather than replacing it, so the
    // user can dictate additions to a half-typed message.
    const anchor = chatInput;
    r.onresult = (e: any) => {
      let combined = '';
      for (let i = 0; i < e.results.length; i++) {
        combined += e.results[i][0].transcript;
      }
      const sep = anchor && combined && !/\s$/.test(anchor) ? ' ' : '';
      setChatInput(anchor + sep + combined);
    };
    r.onerror = (e: any) => {
      console.warn('[SteveChat] SpeechRecognition error:', e?.error);
      setIsListening(false);
    };
    r.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };
    try {
      r.start();
      recognitionRef.current = r;
      setIsListening(true);
    } catch (err: any) {
      console.error('[SteveChat] failed to start SpeechRecognition:', err);
      toast.error(`mic failed: ${err?.message || 'unknown error'}`);
    }
  };

  // Stop listening if the panel closes or component unmounts mid-session.
  useEffect(() => {
    if (!show && isListening) stopListening();
  }, [show, isListening]);
  useEffect(() => () => { stopListening(); }, []);

  // ── Attachments (drag-drop + paste) ─────────────────────────────────
  const [attachments, setAttachments]   = useState<PendingAttachment[]>([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const uploadAttachments = async (files: File[]) => {
    const uid = getAuth().currentUser?.uid ?? 'anon';
    const accepted: PendingAttachment[] = [];
    for (const f of files) {
      if (f.size > ATTACHMENT_MAX_BYTES) {
        toast.warning(`${f.name} is over 10 MB — skipped.`);
        continue;
      }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: f.name || 'pasted-image',
        size: f.size,
        contentType: f.type || 'application/octet-stream',
        uploading: true,
      });
    }
    if (accepted.length === 0) return;
    setAttachments((prev) => [...prev, ...accepted]);

    accepted.forEach(async (rec, idx) => {
      const file = files[idx];
      const path = `andrei_attachments/${uid}/${rec.id}-${rec.name}`;
      try {
        const r = storageRef(storage, path);
        await uploadBytes(r, file, { contentType: rec.contentType });
        const url = await getDownloadURL(r);
        setAttachments((prev) => prev.map((a) => a.id === rec.id ? {
          ...a, url, storagePath: path, uploading: false,
        } : a));
      } catch (err: any) {
        console.error('[AndreiChatPanel] attachment upload failed:', err);
        setAttachments((prev) => prev.map((a) => a.id === rec.id ? {
          ...a, uploading: false, error: err?.message || 'upload failed',
        } : a));
        toast.error(`upload failed: ${rec.name}`);
      }
    });
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  // Window-level drag/drop wrapper — we want the drop zone to cover the
  // entire panel without each child needing its own onDragOver. Counter
  // ref prevents flicker when entering/leaving nested children.
  const dragCounterRef = useRef(0);
  const handleDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingFile(true);
  };
  const handleDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
  };
  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingFile(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) void uploadAttachments(files);
  };

  // Paste handler: clipboard images / files. Only intercepts when there
  // are file blobs; text paste continues normally.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter(Boolean) as File[];
    if (files.length > 0) {
      e.preventDefault();
      void uploadAttachments(files);
    }
  };

  const handleDecide = async (id: string, decision: 'approve' | 'deny') => {
    if (actingId) return;
    setActingId(id);
    try {
      const resp = await authFetch(DECIDE_ACTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, decision }),
      });
      const data = (await resp.json()) as any;
      if (data?.ok) {
        toast.success(decision === 'approve' ? 'approved' : 'denied');
      } else {
        toast.error(`${decision} failed: ${data?.error || 'unknown'}`);
      }
    } catch (err: any) {
      console.error('[AndreiChatPanel] decide failed:', err);
      toast.error(`${decision} failed: ${err?.message || 'network error'}`);
    } finally {
      setActingId(null);
    }
  };

  // Subscribe to messages while authed (the panel is mounted at App
  // level so it lives across the login boundary). Runs even when the
  // panel is closed so we can play the reply chime when the user is
  // elsewhere; tears down on sign-out so we don't get permission-denied
  // log noise.
  useEffect(() => {
    if (!authedUid) {
      // Reset on logout so a future login starts with a clean slate.
      setLiveMessages([]);
      setOlderMessages([]);
      setHasMoreOlder(true);
      return;
    }
    // Session-scoped subscription: when sessionId is set, the live
    // listener pulls only that session's messages so switching from
    // one customer's conversation to another's doesn't bleed history.
    // When sessionId is null we pull the global rolling feed and
    // post-filter to drop any session-scoped docs (mirrors the
    // trigger's history-loader behaviour exactly).
    const baseRef = collection(db, 'andrei_messages');
    // SECURITY: the global (non-session) feed MUST be scoped to the signed-in
    // user — without the userUid filter every authed user saw everyone else's
    // chats. Session feeds are already private by their unguessable sessionId
    // (and firestore.rules enforces per-user ownership on both). Needs the
    // composite index {userUid ASC, ts DESC} (see firestore.indexes.json).
    // BOTH feeds must filter by userUid: the per-user firestore.rule evaluates
    // every doc against userUid==auth.uid, so a query that doesn't constrain
    // userUid is rejected wholesale (even a session query). Session feed adds
    // sessionId on top. Needs index {sessionId,userUid,ts} + {userUid,ts}.
    const q = sessionId
      ? query(baseRef, where('userUid', '==', authedUid), where('sessionId', '==', sessionId), orderBy('ts', 'desc'), limit(100))
      : query(baseRef, where('userUid', '==', authedUid), orderBy('ts', 'desc'), limit(100));
    // Reset paged history when the subscription is rebuilt so old
    // session messages don't linger after a switch.
    setOlderMessages([]);
    setHasMoreOlder(true);
    const unsub = onSnapshot(q, (snap) => {
      // Filter out docs the server-side compaction has marked archived.
      // The trigger's history-loader does the same filter — without it
      // here, post-compaction the originals stay rendered AND counted
      // toward the context gauge, so the gauge "doesn't shrink"
      // after a manual compaction even though the server-side
      // conversation has been compacted. We can't filter at the
      // Firestore query layer because legacy docs don't have the
      // field at all and `!= true` would exclude them (Firestore
      // treats missing as null which is not unequal to true... but
      // also wouldn't index docs without the field).
      const rows: ChatMessage[] = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<ChatMessage, 'id'>) }))
        .filter((m) => (m as any).archived !== true)
        // Global feed must hide session-scoped docs so a customer
        // thread doesn't bleed into the general chat.
        .filter((m) => sessionId ? true : !(m as any).sessionId);
      rows.reverse();
      setLiveMessages(rows);
    }, (err) => {
      console.error('[AndreiChatPanel] subscribe failed:', err);
    });
    return () => unsub();
  }, [authedUid, sessionId]);

  // ── Resume the last general conversation ────────────────────────────
  // Every chat now lives in a session (the orphaned-conversation fix mints one
  // on first send), but the panel still opens to the legacy "global" feed which
  // ONLY shows session-less docs — so after a reload the header chat looked
  // empty even though the conversation was safely saved in its session. On a
  // fresh, context-free open we find the user's most recent GENERAL message
  // (one with a sessionId but no customer/job context) and resume that thread,
  // so history persists across reloads. Runs once per mount, and never when the
  // panel was opened straight into a customer/job context or an explicit
  // session — so "new chat" and customer threads are unaffected.
  const didResumeGeneralRef = useRef(false);
  useEffect(() => {
    if (didResumeGeneralRef.current || !authedUid) return;
    if (context || sessionId) { didResumeGeneralRef.current = true; return; }
    didResumeGeneralRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'andrei_messages'),
          where('userUid', '==', authedUid),
          orderBy('ts', 'desc'),
          limit(25),
        ));
        const recentGeneral = snap.docs
          .map((d) => d.data() as any)
          .find((m) => m && m.sessionId && !m.context);
        if (!cancelled && recentGeneral?.sessionId) setSessionId(recentGeneral.sessionId);
      } catch (e) {
        console.warn('[AndreiChatPanel] resume general session failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [authedUid, context, sessionId, setSessionId]);

  // ── Reply chime ─────────────────────────────────────────────────────
  // Ding on every new assistant message that lands in status:'done'
  // (typing-state docs don't ding — only the final reply does, even
  // though they're the same Firestore doc that mutates in place).
  // Uses a ref to dedupe so re-renders don't replay sounds, and skips
  // the initial load — we only want to chime for new arrivals.
  const seenDoneIdsRef = useRef<Set<string>>(new Set());
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (!bootstrappedRef.current) {
      messages.forEach((m) => {
        if (m.from === 'assistant' && m.status !== 'typing') {
          seenDoneIdsRef.current.add(m.id);
        }
      });
      bootstrappedRef.current = true;
      return;
    }
    const newDone = messages.filter((m) =>
      m.from === 'assistant'
      && m.status !== 'typing'
      && !seenDoneIdsRef.current.has(m.id)
    );
    if (newDone.length === 0) return;
    newDone.forEach((m) => seenDoneIdsRef.current.add(m.id));
    void playReplyChime();
  }, [messages]);

  // Switching threads must re-bootstrap the chime tracker: clear seen ids and
  // un-bootstrap so the newly-opened thread's existing replies are marked seen
  // (no chime) instead of dinging for messages the user just navigated to, or
  // a stale chime firing for the previous thread.
  useEffect(() => {
    seenDoneIdsRef.current = new Set();
    bootstrappedRef.current = false;
  }, [sessionId]);

  // ── Auto-scroll on open ─────────────────────────────────────────────
  // Always jump to the latest message when the panel opens — `requestAnimationFrame`
  // runs after the next paint so the scroll height reflects rendered content.
  useEffect(() => {
    if (!show) return;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // ── Auto-scroll on new arrival ──────────────────────────────────────
  // Scroll to bottom whenever a new message lands at the tail (own send,
  // or Steve's typing/done doc). Pagination prepends to the head and
  // doesn't change the last id, so this won't fire for that case —
  // loadOlder() preserves scroll position on its own.
  const lastTailIdRef = useRef<string | null>(null);
  const lastTailLenRef = useRef<number>(0);
  useEffect(() => {
    if (!show) return;
    const last = messages[messages.length - 1];
    if (!last) return;
    const tailLen = (last.text || '').length;
    const isNewTail = last.id !== lastTailIdRef.current;
    // A NEW tail message (own send / typing doc) always scrolls. The SAME
    // tail growing (Andrei's reply text streaming into the typing doc under
    // one id) only scrolls when the user is already near the bottom — so a
    // long answer follows into view without yanking the page while someone
    // is scrolled up reading earlier context.
    if (!isNewTail && tailLen === lastTailLenRef.current) return;
    const el = scrollRef.current;
    const nearBottom = !el || (el.scrollHeight - el.scrollTop - el.clientHeight < 140);
    lastTailIdRef.current = last.id;
    lastTailLenRef.current = tailLen;
    if (isNewTail || nearBottom) {
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    }
  }, [messages, show]);

  // ── Mobile keyboard: ride the composer above the on-screen keyboard ────
  // iOS Safari doesn't shrink the layout viewport when the keyboard opens,
  // so a 100dvh panel keeps its bottom (the composer) behind the keyboard.
  // Track visualViewport height into a CSS var the panel consumes on mobile.
  useEffect(() => {
    if (!show || !isCoarse) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => {
      document.documentElement.style.setProperty('--andrei-vvh', `${Math.round(vv.height)}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      document.documentElement.style.removeProperty('--andrei-vvh');
    };
  }, [show, isCoarse]);

  // ── Body scroll-lock while the full-screen mobile sheet is open ────────
  // Without this, touchmove on the header/composer/tray scroll-chains to the
  // page behind (iOS rubber-band) and pulling down the transcript triggers
  // Android pull-to-refresh, reloading mid-conversation. ONLY on the narrow
  // full-screen sheet — a docked 480px desktop panel leaves the page visible
  // and it should stay scrollable; skip when floating (small window).
  useEffect(() => {
    if (!show || isFloating || !isNarrow) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [show, isFloating, isNarrow]);

  // ── Android/gesture Back closes the panel (not the route) ──────────────
  // Push a history entry on open so hardware/gesture Back pops it and closes
  // the panel instead of navigating away from the app.
  const didPushRef = useRef(false);
  useEffect(() => {
    if (!show) return;
    // Guard against a StrictMode/double-invoke pushing twice: only push (and
    // only self-collapse) when THIS effect owns the top entry.
    window.history.pushState({ andreiPanel: true }, '');
    didPushRef.current = true;
    const onPop = () => { didPushRef.current = false; onHide(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // If we're tearing down while OUR pushed entry is still on top (panel
      // closed via [×]/Esc, not Back), collapse it so Back doesn't need two
      // presses. If Back already popped it (didPushRef cleared), do nothing.
      if (didPushRef.current && window.history.state && (window.history.state as any).andreiPanel) {
        didPushRef.current = false;
        window.history.back();
      }
    };
  }, [show, onHide]);

  // ── Pagination: load older messages on scroll-near-top ─────────────
  const loadOlder = async () => {
    if (loadingOlder || !hasMoreOlder) return;
    const oldest = messages[0];
    if (!oldest?.ts) {
      // No anchor yet (live snapshot still loading). Defer.
      return;
    }
    setLoadingOlder(true);
    try {
      // Mirror the live subscription's session scoping when paging
      // older messages — otherwise scrolling up in a customer thread
      // would suddenly inject global-feed history.
      const baseRef = collection(db, 'andrei_messages');
      // Same per-user scoping as the live listener (security + matching index).
      const q = sessionId
        ? query(baseRef, where('userUid', '==', authedUid), where('sessionId', '==', sessionId), where('ts', '<', oldest.ts), orderBy('ts', 'desc'), limit(50))
        : query(baseRef, where('userUid', '==', authedUid), where('ts', '<', oldest.ts), orderBy('ts', 'desc'), limit(50));
      const snap = await getDocs(q);
      // Same archived-filter as the live listener so compacted
      // history doesn't reappear when the user scrolls up.
      const rows: ChatMessage[] = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<ChatMessage, 'id'>) }))
        .filter((m) => (m as any).archived !== true)
        // Drop session-scoped docs when paging the global feed.
        .filter((m) => sessionId ? true : !(m as any).sessionId);
      rows.reverse(); // ASC for prepend
      if (snap.docs.length < 50) setHasMoreOlder(false);
      if (rows.length === 0) return;

      // Preserve scroll position: capture current measurements, prepend,
      // then restore on the next frame so the user's view stays anchored
      // to the same logical content rather than getting yanked.
      const el = scrollRef.current;
      const prevHeight = el?.scrollHeight ?? 0;
      const prevTop    = el?.scrollTop ?? 0;
      setOlderMessages((prev) => [...rows, ...prev]);
      requestAnimationFrame(() => {
        const after = scrollRef.current;
        if (!after) return;
        after.scrollTop = after.scrollHeight - prevHeight + prevTop;
      });
    } catch (err: any) {
      console.error('[AndreiChatPanel] loadOlder failed:', err);
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop < 100 && hasMoreOlder && !loadingOlder) {
      void loadOlder();
    }
  };

  // overrideText is used by the inline [retry] on a failed/empty reply — it
  // re-sends the originating message as a fresh, conscious turn (the trigger
  // deliberately never auto-retries, since a turn may have run write tools).
  const sendChatMessage = async (overrideText?: string) => {
    const isRetry = typeof overrideText === 'string';
    const text = (isRetry ? overrideText! : chatInput).trim();
    // A retry never re-attaches the composer's current files.
    const readyAttachments = isRetry ? [] : attachments.filter((a) => a.url && !a.error);
    const stillUploading   = isRetry ? false : attachments.some((a) => a.uploading);
    if ((!text && readyAttachments.length === 0) || sendingChat) return;
    if (stillUploading) {
      toast.info('Wait for attachments to finish uploading.');
      return;
    }
    if (!agentReady) {
      toast.warning('Sign in before chatting with Andrei.');
      return;
    }
    setSendingChat(true);
    try {
      const uid = getAuth().currentUser?.uid ?? null;
      // Orphaned-conversation fix: a chat started from the header had
      // sessionId=null, so the trigger never wrote a session doc and the
      // thread appeared in NO history list and could never be resumed. On the
      // first send of an unscoped chat, mint a session + its andrei_sessions
      // doc (mirrors ClientConversationsTab.handleNew) so every conversation
      // is trackable and resumable. The trigger fills title/preview after.
      let activeSessionId = sessionId;
      if (!activeSessionId) {
        activeSessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        try {
          await setDoc(doc(db, 'andrei_sessions', activeSessionId), {
            id: activeSessionId,
            ...(context ? { customerId: context.id } : {}),
            userUid: uid,
            title: '',
            createdAt: serverTimestamp(),
            lastUpdatedAt: serverTimestamp(),
            lastMessagePreview: '',
          });
          setSessionId(activeSessionId); // future sends + the live listener track it
        } catch (e) {
          // Non-fatal: fall back to sending unscoped rather than blocking the
          // message; worst case it's a legacy-style global-feed message.
          console.warn('[AndreiChatPanel] could not mint session doc:', e);
          activeSessionId = null;
        }
      }
      const payload: Record<string, any> = {
        from: 'user',
        text,
        ts: serverTimestamp(),
        channel: 'in-app',
        processed: false,
        userUid: uid,
        // Provider routing — picked up by onAndreiMessageCreated to
        // decide which LLM handles this turn. See trigger comment
        // for the supported values.
        provider,
      };
      // Attach the active context handle so Andrei's cloud function can scope
      // the answer (e.g. "this is about job 1234"). Sticky across
      // messages until the user clicks the chip's X.
      if (context) payload.context = context;
      // Session id — scopes the conversation thread. Always set now (minted
      // above if it wasn't already), so no conversation is orphaned.
      if (activeSessionId) payload.sessionId = activeSessionId;
      if (readyAttachments.length > 0) {
        payload.attachments = readyAttachments.map((a) => ({
          name:        a.name,
          size:        a.size,
          contentType: a.contentType,
          url:         a.url,
          storagePath: a.storagePath,
        }));
      }
      await addDoc(collection(db, 'andrei_messages'), payload);
      if (!isRetry) { setChatInput(''); setAttachments([]); }
    } catch (err: any) {
      console.error('[AndreiChatPanel] send failed:', err);
      toast.error(`send failed: ${err?.message || 'unknown error'}`);
    } finally {
      setSendingChat(false);
      // Keep the composer focused so the next message needs no extra click.
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  };

  // Re-send the message that produced a failed/empty reply (one-click re-ask).
  const retryReply = (errMsg: ChatMessage) => {
    let text = '';
    const byId = messages.find((x) => x.id === errMsg.inReplyTo && x.from === 'user');
    if (byId?.text) {
      text = byId.text;
    } else {
      const idx = messages.findIndex((x) => x.id === errMsg.id);
      for (let i = idx - 1; i >= 0; i--) {
        if (messages[i].from === 'user' && messages[i].text) { text = messages[i].text as string; break; }
      }
    }
    if (!text.trim()) { toast.info('Retype your message to retry.'); return; }
    void sendChatMessage(text);
  };

  // Andrei is "thinking" when the most recent message is from the user
  // and the trigger hasn't marked it processed yet. Once Andrei writes
  // his own typing-doc, the in-bubble dots take over.
  const andreiThinking = useMemo(() => {
    const last = messages[messages.length - 1];
    return !!(last && last.from === 'user' && !last.processed);
  }, [messages]);

  // Auto-grow the composer textarea up to a max height. Resets on send.
  const TEXTAREA_MAX_PX = 180;
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, TEXTAREA_MAX_PX) + 'px';
  }, [chatInput]);

  // Keyboard contract:
  //   Enter         → send (mirrors single-line behavior people expect)
  //   Shift+Enter   → newline
  // Send is suppressed while a send is in flight or while composing in an IME.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter') return;
    if ((e.nativeEvent as any).isComposing) return; // mid-IME
    // On touch keyboards Enter must insert a newline — there's no Shift key to
    // reach mid-typing, so Enter-to-send would make multi-line messages
    // impossible and fire half-typed ones. The [send] button is the send path.
    // Coarse pointer (not mere narrow width) = a real soft keyboard.
    if (isCoarse) return;
    if (e.shiftKey) return; // newline
    e.preventDefault();
    void sendChatMessage();
  };

  // ── Context-usage indicator (added 2026-05) ─────────────────────────
  // Small circular gauge in the header showing how much of the
  // compaction budget the live history is currently consuming. The
  // server side runs the same estimate (chars/4) and triggers
  // auto-compaction when it crosses HARD_TOKEN_THRESHOLD (85k tokens).
  // Mirroring that math here keeps the gauge accurate without needing
  // a server round-trip.
  //
  // Source-of-truth constants are in functions/src/services/llm/
  // compaction.ts — if you change them there, change them here too.
  //
  // The useMemo MUST sit above the auth early-return below, otherwise
  // React's hook order varies between renders (called when authed,
  // skipped when not). Constants are just literals so they can live
  // wherever; the hook is the part that matters.
  const COMPACT_CHARS_PER_TOKEN = 4;
  const COMPACT_THRESHOLD_TOKENS = 85_000;

  const contextTokens = useMemo(() => {
    let total = 0;
    for (const m of messages) {
      if (m.text) total += Math.ceil(m.text.length / COMPACT_CHARS_PER_TOKEN);
      // per-message overhead matching server estimator
      total += 4;
    }
    return total;
  }, [messages]);

  // Percent of the COMPACT budget (not the model max). 100% = about to
  // auto-compact. We bucket the gauge color: green up to 50%, amber to
  // 80%, red above. The threshold is the compaction trigger, not the
  // model max, because the model max is irrelevant in practice — we
  // never reach it (compaction fires first).
  const contextPercent = Math.min(100, Math.round((contextTokens / COMPACT_THRESHOLD_TOKENS) * 100));
  const contextBucket: 'cool' | 'warm' | 'hot' =
    contextPercent < 50 ? 'cool' : contextPercent < 80 ? 'warm' : 'hot';

  // Manual compaction trigger. Calls the `triggerAndreiCompaction`
  // callable which force-runs the same compaction module the trigger
  // runs automatically at 85k. Useful when the user wants to free
  // context budget before kicking off a long task.
  const [compacting, setCompacting] = useState(false);

  // ── OpenRouter spend (header chip) ──────────────────────────────────
  // getAndreiSpend returns cumulative usage + spend-since-reset (a baseline
  // stored server-side) + the configured compaction model. Fetched on open
  // and after each completed turn (usage updates once the model call settles).
  const [spend, setSpend] = useState<SpendInfo | null>(null);
  const [spendOpen, setSpendOpen] = useState(false);
  const [spendResetting, setSpendResetting] = useState(false);
  // The detail popover is PORTALED to document.body and fixed-positioned from
  // this rect (captured when the chip is clicked). It can't render in place:
  // the header's overflow:hidden clips anything hanging below it, and inside
  // the docked panel `transform: translateX(0)` would hijack position:fixed.
  const [spendRect, setSpendRect] = useState<DOMRect | null>(null);
  const spendPopRef = useRef<HTMLDivElement | null>(null);

  const toggleSpendPop = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setSpendRect(e.currentTarget.getBoundingClientRect());
    setSpendOpen((v) => !v);
  }, []);

  // The portal outlives the panel's hide animation (it's a body-level child,
  // not clipped/hidden with the panel) — close it whenever the panel closes.
  useEffect(() => { if (!show) setSpendOpen(false); }, [show]);

  // Dismiss the portaled popover on outside tap/click, Escape, or resize
  // (a stale rect after rotation/resize would leave it floating adrift).
  useEffect(() => {
    if (!spendOpen) return;
    const onPointerDown = (ev: PointerEvent) => {
      const t = ev.target as Node;
      if (spendPopRef.current?.contains(t)) return;
      if ((t as HTMLElement | null)?.closest?.('.term-andrei__spend')) return;
      setSpendOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setSpendOpen(false); };
    const onResize = () => setSpendOpen(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [spendOpen]);

  const fetchSpend = useCallback(async () => {
    try {
      const r = await authFetch(SPEND_URL, { method: 'GET' });
      const j = await r.json();
      if (typeof j?.sinceReset === 'number' || j?.ok) {
        setSpend({
          sinceReset: Number(j.sinceReset) || 0,
          total: Number(j.totalUsage) || 0,
          limit: (j.limit === null || j.limit === undefined) ? null : Number(j.limit),
          compactionModel: String(j.compactionModel || ''),
          resetAt: j.resetAt ?? null,
          error: j.error,
        });
      }
    } catch { /* leave prior value; chip just won't update */ }
  }, []);

  // Fetch on open; refetch when a turn finishes (andreiThinking → false).
  useEffect(() => { if (show && authedUid) void fetchSpend(); }, [show, authedUid, fetchSpend]);
  const prevThinking = useRef(andreiThinking);
  useEffect(() => {
    if (prevThinking.current && !andreiThinking && show) void fetchSpend();
    prevThinking.current = andreiThinking;
  }, [andreiThinking, show, fetchSpend]);

  const handleSpendReset = useCallback(async () => {
    if (spendResetting) return;
    if (!window.confirm('Reset the OpenRouter spend counter to $0 from now?\n\nThis only moves the "since reset" baseline — your lifetime spend on the key is unaffected.')) return;
    setSpendResetting(true);
    try {
      const r = await authFetch(SPEND_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'reset' }) });
      const j = await r.json();
      if (!j?.ok) throw new Error(j?.error || 'reset failed');
      setSpend((s) => ({
        sinceReset: 0,
        total: Number(j.totalUsage) || 0,
        limit: (j.limit === null || j.limit === undefined) ? (s?.limit ?? null) : Number(j.limit),
        compactionModel: String(j.compactionModel || s?.compactionModel || ''),
        resetAt: j.resetAt ?? Date.now(),
      }));
      toast.success('Spend counter reset.');
    } catch (e: any) {
      toast.error(`Reset failed: ${e?.message || 'error'}`);
    } finally {
      setSpendResetting(false);
      setSpendOpen(false);
    }
  }, [spendResetting]);

  const handleCompactClick = async () => {
    if (compacting) return;
    const ok = window.confirm(
      `Compact this conversation now?\n\nCurrent: ${contextTokens.toLocaleString()} tokens (${contextPercent}% of the auto-compaction budget).\n\nThis summarises the older messages into a single recap and keeps the most recent 20 verbatim. Original messages are archived (not deleted) and restorable from Firestore if needed.`,
    );
    if (!ok) return;
    setCompacting(true);
    try {
      // HTTP twin via authFetch (the callable 403s at the edge on the custom
      // domain). Pass the active sessionId so a session chat compacts ITS
      // conversation, not the global feed; the server scopes to our uid.
      const resp = await authFetch(COMPACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionId ? { sessionId } : {}),
      });
      // Tolerant parse: an edge timeout/crash page returns a NON-JSON body,
      // and awaiting resp.json() first made the `HTTP ${status}` fallback
      // below unreachable (the SyntaxError won the race).
      let r: {
        ok: boolean;
        compacted: boolean;
        archivedCount: number;
        summaryDocId: string | null;
        reason: string;
        degraded: boolean;
        error?: string;
      } | null = null;
      try { r = await resp.json(); } catch { /* non-JSON body */ }
      if (!resp.ok || !r || r.ok === false) throw new Error(r?.error || `HTTP ${resp.status}`);
      if (r.compacted) {
        toast.success(
          `Compacted ${r.archivedCount} message${r.archivedCount === 1 ? '' : 's'}.${r.degraded ? ' (summariser fell back to a basic recap.)' : ''}`,
        );
      } else {
        // Module's MIN_COMPACT_BATCH/KEEP_RECENT guard: too few
        // messages to bother. We force-bypass the threshold but the
        // minimum-batch guard still applies because compacting two
        // messages is worse than not.
        toast.info('Nothing to compact — too few messages older than the keep-recent window.');
      }
    } catch (err: any) {
      console.error('[AndreiChatPanel] manual compaction failed', err);
      toast.error(`Compaction failed: ${err?.message || 'unknown error'}`);
    } finally {
      setCompacting(false);
    }
  };

  const contextTooltip = [
    `Context: ${contextTokens.toLocaleString()} tokens — ${contextPercent}% of the ${COMPACT_THRESHOLD_TOKENS.toLocaleString()}-token auto-compaction budget`,
    `Compaction summariser: ${spend?.compactionModel ? prettyModel(spend.compactionModel) : 'Gemini Flash'}`,
    '',
    compacting ? 'Compacting…' : 'Click to compact this conversation now.',
  ].join('\n');

  // Don't render anything for unauthenticated visitors. This keeps the
  // panel + backdrop off the login page, and prevents the Firestore
  // listeners from running with no auth (which would just emit a stream
  // of permission-denied warnings anyway).
  if (!authedUid) return null;

  // Floating mode positions the panel by inline style so a single
  // animation frame can update both axes without re-rendering CSS
  // classes. When docked, we leave the style off so the SCSS
  // right:0 / 100vh values take over.
  const floatStyle: React.CSSProperties | undefined = isFloating
    ? {
        left: `${floatRect.x}px`,
        top: `${floatRect.y}px`,
        right: 'auto',
        width: `${floatRect.w}px`,
        // When minimized, force height to auto so the panel snaps to
        // header-only. Body sections are display:none'd via the
        // --minimized SCSS modifier; auto-height lets the panel
        // collapse around just the rendered header.
        height: isMinimized ? 'auto' : `${floatRect.h}px`,
        transform: show ? 'none' : 'translateY(20px)',
        opacity: show ? 1 : 0,
        // CRITICAL (2026-05 bugfix): when the panel is closed in
        // floating mode, opacity drops to 0 but the panel STAYS in the
        // DOM at its float position with its full width × height.
        // Without explicit pointer-events:none, that invisible
        // rectangle keeps eating clicks — users close Andrei and then
        // can't interact with dashboard buttons in the area where the
        // panel "used to be". The docked path doesn't have this
        // problem because it translateX(100%)'s off-screen on close;
        // floating just fades.
        pointerEvents: show ? 'auto' : 'none',
        transition: dragRef.current || resizeRef.current
          ? 'none'
          : 'transform 0.2s ease, opacity 0.2s ease, height 0.18s ease',
      }
    : undefined;

  return (
    <>
      {/* Backdrop only renders for the docked state. Floating mode is
          intentionally non-modal — the user keeps interacting with the
          page underneath, so a backdrop would be in the way. */}
      {!isFloating && (
        <div
          className={'term-andrei__backdrop' + (show ? ' is-open' : '')}
          onClick={onHide}
        />
      )}
      <aside
        className={
          'term-andrei'
          + (show ? ' is-open' : '')
          + (isFloating ? ' term-andrei--floating' : '')
          + (isFloating && isMinimized ? ' term-andrei--minimized' : '')
        }
        style={floatStyle}
        aria-hidden={!show}
        role="dialog"
        aria-label="Andrei chat"
        aria-modal={false}
        // Escape closes the panel — keyboard users had no way to dismiss it.
        onKeyDown={(e) => { if (e.key === 'Escape' && show) { e.stopPropagation(); onHide(); } }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDraggingFile && (
          <div className="term-andrei__dropzone">
            <div className="term-andrei__dropzone-text">drop files to attach</div>
          </div>
        )}
        {/* ── Header ─────────────────────────────────────────────────── */}
        {/* When floating, the header is the drag handle. The pointer
            handlers no-op when docked, so attaching them
            unconditionally is fine. The cursor change is driven by the
            modifier class. */}
        <div
          className={'term-andrei__head' + (isFloating ? ' term-andrei__head--draggable' : '')}
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={handleHeaderPointerUp}
          onPointerCancel={handleHeaderPointerUp}
        >
          <div className="term-andrei__head-left">
            <span className="term-andrei__prompt">~/andrei $</span>
            <span className="term-andrei__title">chat</span>
            {andreiThinking && (
              <span className="term-andrei__head-thinking">
                <span className="term-andrei__head-thinking-dot" />
                thinking…
              </span>
            )}
          </div>
          <div className="term-andrei__head-right" data-no-drag>
            {/* OpenRouter spend chip — spend since the last reset. Click the
                amount for a detail popover (total + reset); the inline ↺ is a
                quick reset on desktop (hidden on narrow, where the popover
                carries it). Hidden entirely if the spend endpoint errored
                (e.g. no OpenRouter key) so the header stays clean. */}
            {spend && !spend.error && (
              <div className="term-andrei__spend" data-no-drag>
                <button
                  type="button"
                  className="term-andrei__spend-amt"
                  onClick={toggleSpendPop}
                  title={`OpenRouter spend since reset: ${fmtUsd(spend.sinceReset)} · total ${fmtUsd(spend.total)}`}
                  aria-label={`OpenRouter spend ${fmtUsd(spend.sinceReset)} since reset. Tap for detail.`}
                >
                  {fmtUsd(spend.sinceReset)}
                </button>
                <button
                  type="button"
                  className="term-andrei__spend-reset"
                  onClick={handleSpendReset}
                  disabled={spendResetting}
                  title="Reset the spend counter to $0 from now"
                  aria-label="Reset spend counter"
                >↺</button>
                {/* Portaled to body: escapes the header's overflow:hidden clip
                    AND the docked panel's translateX transform (which would
                    re-anchor position:fixed). Geometry is inline from the rect
                    captured at click time; z-index clears the panel (1100). */}
                {spendOpen && spendRect && createPortal(
                  <div
                    ref={spendPopRef}
                    className="term-andrei__spend-pop"
                    role="dialog"
                    aria-label="Spend detail"
                    style={{
                      position: 'fixed',
                      top: spendRect.bottom + 6,
                      right: Math.max(8, window.innerWidth - spendRect.right),
                      zIndex: 2000,
                    }}
                  >
                    <div className="term-andrei__spend-pop-row">since reset · <strong>{fmtUsd(spend.sinceReset)}</strong></div>
                    <div className="term-andrei__spend-pop-row">total · <strong>{fmtUsd(spend.total)}</strong>{spend.limit != null ? ` / ${fmtUsd(spend.limit)}` : ''}</div>
                    <button type="button" className="term-andrei__spend-pop-reset" onClick={handleSpendReset} disabled={spendResetting}>↺ reset counter</button>
                  </div>,
                  document.body,
                )}
              </div>
            )}
            {/* Context usage gauge — circular ring that fills as the
                conversation approaches the auto-compaction threshold.
                Inline SVG keeps it themable via currentColor on the
                bucket class. Clickable: triggers manual compaction
                via the triggerAndreiCompaction callable. */}
            <button
              type="button"
              className={
                `term-andrei__context-gauge term-andrei__context-gauge--${contextBucket}`
                + (compacting ? ' term-andrei__context-gauge--busy' : '')
              }
              onClick={handleCompactClick}
              disabled={compacting}
              aria-label={contextTooltip}
              title={contextTooltip}
              data-no-drag
            >
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                {/* Background ring */}
                <circle cx="8" cy="8" r="6" fill="none" strokeWidth="2" className="term-andrei__context-gauge-bg" />
                {/* Foreground arc — stroke-dasharray driven by percent.
                    Circumference = 2 * PI * 6 ≈ 37.7. We use that to
                    map percent to a fraction of the ring. */}
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="term-andrei__context-gauge-fg"
                  style={{
                    strokeDasharray: '37.7',
                    strokeDashoffset: `${37.7 * (1 - contextPercent / 100)}`,
                    // Start the arc at 12 o'clock instead of 3
                    transform: 'rotate(-90deg)',
                    transformOrigin: '50% 50%',
                    transition: 'stroke-dashoffset 0.3s ease',
                  }}
                />
              </svg>
            </button>
            {/* New chat — always available on the general (context-free) chat,
                not gated on an active session (it used to be, so on a fresh open
                — or right after starting one — the button silently vanished).
                Clears the session + composer so a fresh thread starts; the
                current conversation stays saved and resumable from history. */}
            {!context && (
              <button
                type="button"
                className="term-andrei__head-btn"
                onClick={() => { setSessionId(null); setChatInput(''); setAttachments([]); }}
                title="New chat — start a fresh conversation (your current one stays saved)"
                aria-label="New chat"
                data-no-drag
              >[+]</button>
            )}
            {/* Provider selector — same shared dropdown as the
                JobWorkspace chat header. Persisted to localStorage;
                written onto every outgoing andrei_messages doc as
                `provider`. The trigger reads it and routes to the
                corresponding model. */}
            <ProviderSelect
              className="term-andrei__provider"
              value={provider}
              onChange={setProvider}
              noDrag
            />
            {/* Float/dock is a desktop-only affordance — at ≤640px the panel
                is always a full-screen sheet (CSS forces it), so the toggle
                is dead weight competing for scarce header width. */}
            {!isNarrow && (
              <button
                type="button"
                className="term-andrei__head-btn"
                onClick={() => setIsFloating((v) => !v)}
                aria-label={isFloating ? 'Dock to side' : 'Float in window'}
                title={isFloating ? 'Dock to side' : 'Float in window'}
                data-no-drag
              >
                {isFloating ? '[dock]' : '[float]'}
              </button>
            )}
            {/* Minimize only meaningful in floating mode — collapses
                to a header bar so the page underneath is clickable. */}
            {isFloating && (
              <button
                type="button"
                className="term-andrei__head-btn"
                onClick={() => setIsMinimized((v) => !v)}
                aria-label={isMinimized ? 'Restore chat' : 'Minimize chat (frees the screen underneath)'}
                title={isMinimized ? 'Restore' : 'Minimize'}
                data-no-drag
              >
                {isMinimized ? '[□]' : '[─]'}
              </button>
            )}
            <button
              type="button"
              className="term-andrei__close"
              onClick={onHide}
              aria-label="Close chat"
              data-no-drag
            >[×]</button>
          </div>
        </div>

        {/* ── Transcript ─────────────────────────────────────────────── */}
        <div className="term-andrei__scroll" ref={scrollRef} onScroll={handleScroll} role="log" aria-live="polite" aria-atomic="false" aria-label="Conversation">
          {/* Older-pagination affordance: shows while a load is in flight,
              and when we've loaded the entire history. Otherwise nothing
              renders so the empty state stays clean. */}
          {messages.length > 0 && loadingOlder && (
            <div className="term-andrei__older-loading">loading older…</div>
          )}
          {messages.length > 0 && !hasMoreOlder && (
            <div className="term-andrei__older-end">— start of conversation —</div>
          )}
          {messages.length === 0 ? (
            <div className="term-andrei__empty">
              <div className="term-andrei__empty-lead">Andrei can pull Google Ads numbers, summarise a customer, draft an email, or check on jobs &amp; invoices.</div>
              <div className="term-andrei__empty-prompts">
                {[
                  'How are my Google Ads accounts doing this week?',
                  'What search terms wasted money with no conversions?',
                  'Summarise my most recent customer activity.',
                  'Draft a follow-up email to a client.',
                ].map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="term-andrei__empty-prompt"
                    disabled={!agentReady}
                    onClick={() => { setChatInput(p); requestAnimationFrame(() => textareaRef.current?.focus()); }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => {
              const ts = tsToDate(m.ts);
              const isUser = m.from === 'user';
              return (
                <div
                  key={m.id}
                  className={
                    'term-andrei__msg'
                    + (isUser
                        ? ' term-andrei__msg--user'
                        : ' term-andrei__msg--assistant')
                  }
                >
                  <div className="term-andrei__msg-meta">
                    <span className="term-andrei__msg-from">
                      {isUser ? '> you' : '> andrei'}
                    </span>
                    <span className="term-andrei__msg-ts">
                      {ts ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '…'}
                    </span>
                    {!isUser && m.status !== 'typing' && m.text && (
                      <button
                        type="button"
                        className="term-andrei__msg-copy"
                        onClick={() => {
                          const t = m.text || '';
                          if (!navigator.clipboard) {
                            toast.warning('clipboard not available');
                            return;
                          }
                          navigator.clipboard.writeText(t)
                            .then(() => toast.success('copied'))
                            .catch(() => toast.error('copy failed'));
                        }}
                        title="Copy message"
                        aria-label="Copy message"
                      >[copy]</button>
                    )}
                  </div>
                  <div className="term-andrei__msg-text">
                    {!isUser && m.status === 'typing' ? (
                      <span className="term-andrei__typing">
                        <span className="term-andrei__typing-dot" />
                        <span className="term-andrei__typing-dot" />
                        <span className="term-andrei__typing-dot" />
                      </span>
                    ) : isUser ? (
                      // User messages render as plain text — they're unlikely
                      // to use markdown intentionally and we don't want a stray
                      // backtick or asterisk to munge their words.
                      m.text
                    ) : (m.status === 'error' || (m.text || '').startsWith('(error)')) ? (
                      // A failed turn is a distinct card with a one-click retry,
                      // not a raw "(error) …" line the user has to retype past.
                      <div className="term-andrei__msg-error" role="alert">
                        <span className="term-andrei__msg-error-icon" aria-hidden="true">⚠</span>
                        <span className="term-andrei__msg-error-text">
                          {(m as any).error || (m.text || '').replace(/^\(error\)\s*/, '') || 'Andrei couldn’t complete that.'}
                        </span>
                        <button type="button" className="term-andrei__msg-retry" onClick={() => retryReply(m)} disabled={sendingChat}>[retry]</button>
                      </div>
                    ) : (m.text || '').trim() === '(empty response)' ? (
                      <div className="term-andrei__msg-error term-andrei__msg-error--empty">
                        <span className="term-andrei__msg-error-text">No answer came back.</span>
                        <button type="button" className="term-andrei__msg-retry" onClick={() => retryReply(m)} disabled={sendingChat}>[retry]</button>
                      </div>
                    ) : (
                      // Assistant messages get full markdown — Steve replies
                      // with bullet lists, code, tables, links. remark-gfm
                      // covers GFM additions (tables, strikethrough, task
                      // lists, autolinks). Links open in a new tab.
                      <div className="term-andrei__md">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ href, children, ...rest }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {m.text}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="term-andrei__msg-attachments">
                      {m.attachments.map((a, i) => {
                        const isImage = a.contentType.startsWith('image/');
                        return isImage ? (
                          <a
                            key={i}
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="term-andrei__msg-img-link"
                            title={a.name}
                          >
                            <img
                              src={a.url}
                              alt={a.name}
                              className="term-andrei__msg-img"
                              loading="lazy"
                            />
                          </a>
                        ) : (
                          <a
                            key={i}
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="term-andrei__msg-file"
                          >
                            📎 {a.name} <span className="term-andrei__msg-file-size">{(a.size / 1024).toFixed(0)}KB</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {m.toolCalls && m.toolCalls.length > 0 && (
                    <div className="term-andrei__tools">
                      {m.toolCalls.map((tc, i) => (
                        <div key={i} className="term-andrei__tool">
                          <span className="term-andrei__tool-name">⚙ {tc.name}</span>
                          {tc.error && (
                            <span className="term-andrei__tool-err"> · error: {tc.error}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {m.error && (
                    <div className="term-andrei__msg-err">! {m.error}</div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* New chat lives in the header now (the [+] icon) — no dedicated row,
            so the transcript keeps the vertical space. */}

        {/* ── Context chip ───────────────────────────────────────────── */}
        {context && (
          <div className="term-andrei__context">
            <span className="term-andrei__context-label">context:</span>
            <span className="term-andrei__context-handle">
              {context.label || `${context.type} ${context.id}`}
            </span>
            <button
              type="button"
              className="term-andrei__context-clear"
              onClick={() => setContext(null)}
              title="Clear context"
              aria-label="Clear context"
            >[×]</button>
          </div>
        )}

        {/* ── Pending tray ───────────────────────────────────────────── */}
        {pending.length > 0 && (
          <div className="term-andrei__pending">
            <div className="term-andrei__pending-head">
              <span>{pending.length} pending action{pending.length === 1 ? '' : 's'}</span>
            </div>
            <ul className="term-andrei__pending-list">
              {pending.map((p) => {
                const busy = actingId === p.id;
                const target = p.target ? Object.entries(p.target).map(([k, v]) => `${k}=${v}`).join(' ') : '';
                return (
                  <li key={p.id} className="term-andrei__pending-item">
                    <div className="term-andrei__pending-text">
                      <span className="term-andrei__pending-kind">{p.kind}</span>
                      {target && <span className="term-andrei__pending-target"> · {target}</span>}
                      {p.reason && <span className="term-andrei__pending-reason"> · {p.reason}</span>}
                    </div>
                    <div className="term-andrei__pending-actions">
                      <button
                        type="button"
                        className="term-andrei__pending-btn term-andrei__pending-btn--accent"
                        onClick={() => handleDecide(p.id, 'approve')}
                        disabled={busy || !!actingId}
                      >
                        {busy ? '…' : '[approve]'}
                      </button>
                      <button
                        type="button"
                        className="term-andrei__pending-btn term-andrei__pending-btn--danger"
                        onClick={() => handleDecide(p.id, 'deny')}
                        disabled={busy || !!actingId}
                      >
                        [deny]
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* ── Attachment chips ───────────────────────────────────────── */}
        {attachments.length > 0 && (
          <div className="term-andrei__attachments">
            {attachments.map((a) => (
              <div
                key={a.id}
                className={
                  'term-andrei__attachment'
                  + (a.error ? ' term-andrei__attachment--err' : '')
                }
              >
                <span className="term-andrei__attachment-name" title={a.name}>
                  {a.uploading ? '⇡ ' : a.error ? '✗ ' : '📎 '}
                  {a.name}
                </span>
                <span className="term-andrei__attachment-size">
                  {(a.size / 1024).toFixed(0)}KB
                </span>
                <button
                  type="button"
                  className="term-andrei__attachment-remove"
                  onClick={() => removeAttachment(a.id)}
                  aria-label="Remove attachment"
                >[×]</button>
              </div>
            ))}
          </div>
        )}

        {/* ── Composer ──────────────────────────────────────────────────
            Two-row layout (changed 2026-05): textarea takes the full
            panel width on its own row; mic/send actions sit on a
            second row below, right-aligned. The previous single-row
            layout (prompt + textarea + buttons all inline) starved the
            textarea of horizontal space and made the placeholder wrap
            even at typical widths. */}
        <form
          className="term-andrei__form"
          onSubmit={(e) => { e.preventDefault(); sendChatMessage(); }}
        >
          <div className="term-andrei__form-row term-andrei__form-row--input">
            <span className="term-andrei__form-prompt">$</span>
            <textarea
              ref={textareaRef}
              className="term-andrei__input"
              placeholder={
                !agentReady
                  ? 'sign in to chat with andrei'
                  : isCoarse
                    ? 'message andrei…'
                    : 'message andrei… (drop files · enter to send · shift+enter for newline)'
              }
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={sendingChat || !agentReady}
              // Don't pop the keyboard on open for touch — it hides the very
              // transcript the user opened to read. Desktop still auto-focuses.
              autoFocus={show && agentReady && !isCoarse}
              rows={1}
            />
          </div>
          <div className="term-andrei__form-row term-andrei__form-row--actions">
            {/* Attach — the ONLY attachment path on touch (drag-drop and
                paste don't exist on a phone); also a click path for desktop.
                Photos of a site / receipts are the mobile attachment. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.txt,.csv,.md,.doc,.docx"
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) void uploadAttachments(files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="term-andrei__attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={!agentReady || sendingChat}
              title="Attach files or photos"
              aria-label="Attach files or photos"
            >
              [attach]
            </button>
            {speechRecognitionAvailable && (
              <button
                type="button"
                className={
                  'term-andrei__mic'
                  + (isListening ? ' term-andrei__mic--active' : '')
                }
                onClick={() => isListening ? stopListening() : startListening()}
                disabled={!agentReady || sendingChat}
                title={isListening ? 'Stop listening' : 'Dictate (mic)'}
                aria-label={isListening ? 'Stop listening' : 'Dictate'}
                aria-pressed={isListening}
              >
                {isListening ? '[● rec]' : '[mic]'}
              </button>
            )}
            <button
              type="submit"
              className="term-andrei__send"
              disabled={
                (!chatInput.trim() && attachments.filter((a) => a.url).length === 0)
                || sendingChat
                || !agentReady
                || attachments.some((a) => a.uploading)
              }
              title={!agentReady ? 'Sign in to chat' : undefined}
            >
              {sendingChat ? '[sending…]' : '[send]'}
            </button>
          </div>
        </form>

        {/* Resize handle — only rendered in floating mode. Bottom-right
            corner; drag to resize. */}
        {isFloating && (
          <div
            className="term-andrei__resize"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            onPointerCancel={handleResizePointerUp}
            aria-hidden="true"
          />
        )}
      </aside>
    </>
  );
};

export default AndreiChatPanel;
